import React, { useEffect, useState, useCallback } from "react";
import {
  Hidden,
  Drawer,
  Divider,
  Box,
  Toolbar,
  Grid,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useObserver } from "mobx-react-lite";
import { PowerSettingsNewRounded } from "@material-ui/icons";
import Skeleton from "@material-ui/lab/Skeleton";
import lodash from "lodash";
import logo from "../../test-logo.png";
import CustomList from "../Custom/CustomList";
import { firebaseFetchAllDealers } from "../../firebase/dealer";
import { navigate } from "@reach/router";

export default function SideBar(props) {
  /**
  |--------------------------------------------------
  | Props
  |--------------------------------------------------
  */
  const {
    container,
    handleDrawerToggle,
    mobileOpen,
    userProfile,
    dealerID,
  } = props;

  /**
  |--------------------------------------------------
  | States
  |--------------------------------------------------
  */
  const [loading, setLoading] = useState(true);
  const [dealers, setDealers] = useState([]);
  const [logoURL, setLogoURL] = useState("");
  const [loadingIMG, setLoadingIMG] = useState(false);

  /**
  |--------------------------------------------------
  | Fetch Dealers
  |--------------------------------------------------
  */
  const fetchDealers = useCallback(async () => {
    try {
      setLoadingIMG(true);
      const dealers = await firebaseFetchAllDealers();

      let dealersForUser = lodash.map(dealers, (d, key) => ({ ...d, id: key }));
      if (userProfile.role !== "superadmin") {
        dealersForUser = lodash.filter(
          dealersForUser,
          (d) => d.id === userProfile.dealer
        );
      }

      const selectedDealer = lodash.find(dealersForUser, { id: dealerID });
      if (selectedDealer) {
        setLogoURL(selectedDealer.logo);
        setTimeout(() => {
          setLoadingIMG(false);
        }, 400);
      }

      setDealers(dealersForUser);
      setLoading(false);
    } catch (error) {
      console.error(error);
    }
  }, [dealerID, userProfile]);

  useEffect(() => {
    fetchDealers();
  }, [fetchDealers]);

  /**
  |--------------------------------------------------
  | Styles hooks
  |--------------------------------------------------
  */
  const classes = useStyles();

  /**
  |--------------------------------------------------
  | Dealer to List's data
  |--------------------------------------------------
  */
  function dealerList() {
    const dealerList = lodash.map(dealers, (item) => ({
      name: item.name,
      label: item.name,
      href: `/${item.id}`,
    }));
    return dealerList;
  }

  /**
  |--------------------------------------------------
  | Drawer
  |--------------------------------------------------
  */
  const drawer = (
    <div>
      <Toolbar>
        <Grid container>
          <Grid item xs>
            <Box p={2}>
              {loadingIMG ? (
                <Skeleton
                  variant="rect"
                  style={{
                    width: "100%",
                    objectFit: "cover",
                    objectPosition: "center",
                  }}
                  height={33}
                />
              ) : (
                <img
                  src={logoURL || logo}
                  alt="logo"
                  style={{
                    width: "100%",
                    objectFit: "cover",
                    objectPosition: "center",
                  }}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Toolbar>
      <Divider />
      <CustomList
        data={dealerList()}
        selectedDealer={lodash.find(dealers, { id: dealerID })}
      />
      <Grid
        container
        alignItems="center"
        style={{ position: "absolute", bottom: "10px", width: "100%" }}
      >
        <button onClick={() => navigate(`/signout`)} style={{ width: "100%" }}>
          <Box
            color="white"
            px={2}
            py={2}
            width="100%"
            display={{ xs: "flex", sm: "none" }}
          >
            <Grid container alignItems="center">
              <Box mr={1}>
                <PowerSettingsNewRounded />
              </Box>
              <Typography variant="subtitle2">Sign out</Typography>
            </Grid>
          </Box>
        </button>
      </Grid>
    </div>
  );

  /**
  |--------------------------------------------------
  | Render
  |--------------------------------------------------
  */
  return useObserver(() => (
    <nav className={classes.drawer} aria-label="mailbox folders">
      {loading ? (
        <Box
          pt={10}
          justifyContent="center"
          color="white"
          display={{ xs: "none", sm: "block" }}
        ></Box>
      ) : (
        <>
          <Hidden smUp implementation="css">
            <Drawer
              container={container}
              variant="temporary"
              open={mobileOpen}
              onClose={handleDrawerToggle}
              classes={{
                paper: classes.drawerPaper,
              }}
              ModalProps={{
                keepMounted: true, // Better open performance on mobile.
              }}
            >
              {drawer}
            </Drawer>
          </Hidden>
          <Hidden xsDown implementation="css">
            <Drawer
              classes={{
                paper: classes.drawerPaper,
              }}
              variant="permanent"
              open
            >
              {drawer}
            </Drawer>
          </Hidden>
        </>
      )}
    </nav>
  ));
}

/**
|--------------------------------------------------
| Custom Styles
|--------------------------------------------------
*/
const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  drawer: {
    [theme.breakpoints.up("sm")]: {
      width: drawerWidth,
      flexShrink: 0,
    },
  },
  drawerPaper: {
    width: drawerWidth,
  },
}));
